// ============================================================================
// EventForm — UIF event create page.
// ============================================================================
// Routed via #admin-event-create. Form covers everything an event needs:
// title, dates, audience, tags, location, capacity, optional room reservation,
// optional media URLs. Submit calls api.createEvent. On success, navigates
// to the new event's detail page so David can verify.
//
// Field grouping (top-to-bottom):
//   1. Basics      — title, audience, description
//   2. When        — start_date, end_date, registration_deadline
//   3. Where       — location_details, venue_details, resource_id (room)
//   4. Capacity    — max_attendees, materials, event_type, source_reference_key
//   5. Tags        — multi-select pill row from /api/tags
//   6. Media       — hero_photo_url, video_embed_url
//
// Future (Phase 3d-1b): edit existing event via #admin-event-edit/:id.
// ============================================================================
import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, CheckCircle2, Plus, ArrowRight } from 'lucide-react';
import {
  api,
  type Audience,
  type EventTag,
  type Provision,
  type SharedResource,
} from '../api';
import { useAuth } from '../auth';
import { PresenterLibrary } from './PresenterLibrary';
import { UploadPhoto } from './UploadPhoto';
import { LocationLibrary } from './LocationLibrary';
import { SeriesPicker } from './SeriesPicker';
import { PrerequisitePicker } from './PrerequisitePicker';
import { DaysSection } from './DaysSection';
import { EventEvidenceUpload as EvidenceUploadInline } from './EventEvidenceUpload';
import type { EventDay } from '../api';

const PROVISION_OPTIONS: { value: Provision; label: string }[] = [
  { value: 'breakfast', label: 'Breakfast provided' },
  { value: 'lunch',     label: 'Lunch provided' },
  { value: 'dinner',    label: 'Dinner provided' },
  { value: 'snacks',    label: 'Snacks & coffee' },
];

interface EventFormProps {
  // When set, the form loads the existing event and saves via api.updateEvent.
  // When undefined, the form creates a new event via api.createEvent.
  eventId?: number;
}

const AUDIENCE_OPTIONS: { value: Audience; label: string; description: string }[] = [
  { value: 'public',   label: 'Open to public',           description: 'Anyone with the link can see + register' },
  { value: 'amp',      label: 'AMP Producers only',       description: 'Visible after AMP-group login' },
  { value: 'agc',      label: 'AGC only',                 description: 'Visible after AGC-group login' },
  { value: 'internal', label: 'Internal · UIF only',      description: 'Visible only to UIF team members' },
];

function toISO(local: string): string {
  // Convert HTML datetime-local (e.g. '2026-07-13T08:30') to ISO with Z.
  // We treat the input as already-in-Central-time per the rest of the app's
  // naive-timestamp convention.
  if (!local) return '';
  // Append :00 seconds if missing, then Z suffix.
  const withSeconds = local.length === 16 ? `${local}:00` : local;
  return `${withSeconds}Z`;
}

export function EventForm({ eventId }: EventFormProps = {}) {
  const { token } = useAuth();
  const isEditMode = eventId !== undefined;
  const [loadingEvent, setLoadingEvent] = useState(isEditMode);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState<Audience>('public');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [registrationDeadline, setRegistrationDeadline] = useState('');
  const [location, setLocation] = useState('Up in Farms Training Center, 2093 Lakeland Dr, Jackson, MS 39216');
  const [venueDetails, setVenueDetails] = useState('');
  const [resourceId, setResourceId] = useState<number | null>(null);
  // Task #15: online + hybrid event delivery.
  const [format, setFormat] = useState<'in_person' | 'online' | 'hybrid'>('in_person');
  const [onlineProvider, setOnlineProvider] = useState<'zoom' | 'teams' | 'meet' | 'webex' | 'other' | ''>('');
  const [onlineMeetingUrl, setOnlineMeetingUrl] = useState('');
  const [onlineMeetingId, setOnlineMeetingId] = useState('');
  const [onlineMeetingPasscode, setOnlineMeetingPasscode] = useState('');
  const [onlineJoinInstructions, setOnlineJoinInstructions] = useState('');
  const [onlineUrlVisibility, setOnlineUrlVisibility] = useState<'public' | 'registered_only' | 'farmer_signed_in'>('registered_only');
  const [onlineMaxAttendees, setOnlineMaxAttendees] = useState<number | ''>('');
  // Task #3: bookable staff resources. The picker shows only resource_type
  // ='staff' rows; each selection creates a per-day booking that conflict-
  // checks against the same GIST exclusion constraint as rooms.
  const [staffResourceIds, setStaffResourceIds] = useState<Set<number>>(new Set());
  const [maxAttendees, setMaxAttendees] = useState<number | ''>('');
  const [materials, setMaterials] = useState('');
  const [eventType, setEventType] = useState('Workshop');
  const [sourceKey, setSourceKey] = useState('');
  const [sourceKeyManual, setSourceKeyManual] = useState(false);

  // Phase 027: persistent URL slug. Auto-suggested from title + date until
  // the user edits it manually.
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);

  // Auto-suggest source_reference_key from title + start date so most users
  // can ignore it. Format: <slug>-YYYY-MM (e.g. "conservation-workshop-2026-07").
  // Only auto-fills until the user types in the field manually.
  useEffect(() => {
    if (sourceKeyManual) return;
    if (!title || !startDate) return;
    const slugForKey = title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    const ym = startDate.slice(0, 7); // YYYY-MM from datetime-local
    setSourceKey(`${slugForKey}-${ym}`);
  }, [title, startDate, sourceKeyManual]);

  // Auto-suggest URL slug from title + start date. Format: <kebab>-YYYYMMDD.
  useEffect(() => {
    if (slugManual) return;
    if (!title || !startDate) return;
    const kebab = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    const ymd = startDate.slice(0, 10).replace(/-/g, ''); // YYYYMMDD
    setSlug(`${kebab}-${ymd}`);
  }, [title, startDate, slugManual]);

  const [selectedTags, setSelectedTags] = useState<Set<number>>(new Set());
  const [heroPhotoUrl, setHeroPhotoUrl] = useState('');
  const [videoEmbedUrl, setVideoEmbedUrl] = useState('');

  // Phase 3d-1d attribute fields
  const [costText, setCostText] = useState('');
  const [stipendText, setStipendText] = useState('');
  const [doorsOpenText, setDoorsOpenText] = useState('');

  // Phase 3d-1e provisions (set of selected meal/refreshment values)
  const [provisions, setProvisions] = useState<Set<Provision>>(new Set());

  // Phase 3d-1c per-event modal markdown
  const [agendaMd, setAgendaMd] = useState('');
  const [aboutMd, setAboutMd] = useState('');
  const [presentersMd, setPresentersMd] = useState('');

  // Phase 028: per-day rows
  const [days, setDays] = useState<EventDay[]>([]);

  // Round 3: Recurring Event template fields
  const [isRecurringTemplate, setIsRecurringTemplate] = useState(false);
  const [parentTemplateId, setParentTemplateId] = useState<number | null>(null);
  // Round 4: Draft mode. New events default to draft (true). Edit mode
  // pre-fills from the loaded event below. Admins toggle via the checkbox
  // in the submit row, or use the Go Live button on the detail page.
  const [isDraft, setIsDraft] = useState(true);
  // Round 4: after a successful CREATE, surface a success card with three
  // choices: view the event, add another, or back to events list. We hold
  // off on the navigate so the admin gets the choice. Edit mode still
  // redirects directly to the slug URL like before.
  const [lastCreated, setLastCreated] = useState<{ slug: string | null; title: string; isDraft: boolean; isTemplate: boolean } | null>(null);
  const [instanceNotes, setInstanceNotes] = useState('');

  // Phase 026: series + prereq
  const [seriesId, setSeriesId] = useState<number | null>(null);
  const [sequenceNumber, setSequenceNumber] = useState<number | null>(null);
  const [instanceLabel, setInstanceLabel] = useState('');
  const [prereqEventIds, setPrereqEventIds] = useState<number[]>([]);
  const [prereqText, setPrereqText] = useState('');

  // Fetched lists
  const [tags, setTags] = useState<EventTag[]>([]);
  const [resources, setResources] = useState<SharedResource[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    api.tags().then((d) => setTags(d.tags ?? [])).catch(() => {});
    if (token) {
      api.sharedResources(token).then((d) => setResources(d.resources ?? [])).catch(() => {});
    }
  }, [token]);

  // Edit mode: load existing event and pre-fill the form. Waits until tags are
  // loaded so we can map tag names → tag IDs for the pill picker state.
  useEffect(() => {
    if (!isEditMode || !eventId) return;
    if (tags.length === 0) return; // wait for tag list
    let cancelled = false;
    // includeTemplates + includeDrafts so admins can edit recurring templates
    // AND draft events from the Events panel — both are filtered out of the
    // default /api/events response (tasks #33 and #40 fixes). Without these
    // flags, clicking Edit on a template or draft row would 404 here.
    api
      .events({ includePast: true, includeTemplates: true, includeDrafts: true, token: token ?? undefined })
      .then((d) => {
        if (cancelled) return;
        const ev = (d.events ?? []).find((e) => e.calendar_entry_id === eventId || e.event_id === eventId);
        if (!ev) {
          setError('Event not found or not visible to your role.');
          setLoadingEvent(false);
          return;
        }
        // Pre-fill all fields. datetime-local expects 'YYYY-MM-DDTHH:mm'.
        const toLocal = (iso: string) => iso.slice(0, 16);
        setTitle(ev.title);
        setDescription(ev.description ?? '');
        setAudience((ev.audience as Audience) ?? 'public');
        setStartDate(toLocal(ev.start_date));
        setEndDate(toLocal(ev.end_date));
        setRegistrationDeadline(ev.registration_deadline ? toLocal(ev.registration_deadline) : '');
        setLocation(ev.location ?? '');
        setVenueDetails(ev.venue_details ?? '');
        setMaxAttendees(ev.max_attendees ?? '');
        setMaterials(ev.materials ?? '');
        setEventType(ev.event_type ?? 'Workshop');
        setHeroPhotoUrl(ev.hero_photo_url ?? '');
        setVideoEmbedUrl(ev.video_embed_url ?? '');
        setCostText(ev.cost_text ?? '');
        setStipendText(ev.stipend_text ?? '');
        setDoorsOpenText(ev.doors_open_text ?? '');
        setProvisions(new Set((ev.provisions ?? []) as Provision[]));
        setAgendaMd(ev.agenda_md ?? '');
        setAboutMd(ev.about_md ?? '');
        setPresentersMd(ev.presenters_md ?? '');
        setResourceId(ev.resource_id ?? null);
        // Task #3: pre-fill staff multi-select on edit.
        setStaffResourceIds(new Set(ev.staff_resource_ids ?? []));
        // Task #15: pre-fill format + online fields on edit.
        setFormat(ev.format ?? 'in_person');
        setOnlineProvider(ev.online_provider ?? '');
        setOnlineMeetingUrl(ev.online_meeting_url ?? '');
        setOnlineMeetingId(ev.online_meeting_id ?? '');
        setOnlineMeetingPasscode(ev.online_meeting_passcode ?? '');
        setOnlineJoinInstructions(ev.online_join_instructions ?? '');
        setOnlineUrlVisibility(ev.online_url_visibility ?? 'registered_only');
        setOnlineMaxAttendees(ev.online_max_attendees ?? '');
        setSeriesId(ev.series_id ?? null);
        setSlug(ev.slug ?? '');
        setSlugManual(true);
        setDays(ev.days ?? []);
        setIsRecurringTemplate(ev.is_recurring_template ?? false);
        setIsDraft(ev.is_draft ?? false);
        setParentTemplateId(ev.parent_template_id ?? null);
        setInstanceNotes(ev.instance_notes ?? '');
        setSequenceNumber(ev.sequence_number ?? null);
        setInstanceLabel(ev.instance_label ?? '');
        setPrereqEventIds(ev.prereq_event_ids ?? []);
        setPrereqText(ev.prereq_text ?? '');
        // Map tag names → tag IDs
        const tagSet = new Set<number>();
        for (const tagName of ev.tags ?? []) {
          const matched = tags.find((t) => t.tag_name === tagName);
          if (matched) tagSet.add(matched.tag_id);
        }
        setSelectedTags(tagSet);
        // We don't expose source_reference_key on the public view, so leave
        // sourceKey blank in edit mode (auto-suggest won't fire because the
        // sourceKeyManual flag will be set on the first user keystroke).
        // resource_id isn't available on the public event view either —
        // editing room reservation requires a separate flow for now.
        setSourceKeyManual(true);
        setLoadingEvent(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Could not load event.');
        setLoadingEvent(false);
      });
    return () => { cancelled = true; };
  }, [isEditMode, eventId, token, tags]);

  const toggleTag = (id: number) => {
    const next = new Set(selectedTags);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedTags(next);
  };

  const toggleProvision = (value: Provision) => {
    const next = new Set(provisions);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setProvisions(next);
  };

  const handleBack = () => {
    window.location.hash = isEditMode && eventId ? `#event/${eventId}` : '#admin-dashboard';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !token) return;
    if (!startDate || !endDate) {
      setError('Start and end dates are required.');
      return;
    }
    if (new Date(endDate) <= new Date(startDate)) {
      setError('End date must be after start date.');
      return;
    }
    setSubmitting(true);
    setError('');
    const payload = {
      entry_title: title.trim(),
      entry_description: description.trim() || null,
      start_date: toISO(startDate),
      end_date: toISO(endDate),
      audience,
      location_details: location.trim() || null,
      venue_details: venueDetails.trim() || null,
      event_type: eventType.trim() || 'Workshop',
      max_attendees: maxAttendees === '' ? null : Number(maxAttendees),
      registration_deadline: registrationDeadline ? toISO(registrationDeadline) : null,
      materials: materials.trim() || null,
      source_reference_key: sourceKey.trim() || null,
      hero_photo_url: heroPhotoUrl.trim() || null,
      video_embed_url: videoEmbedUrl.trim() || null,
      cost_text: costText.trim() || null,
      stipend_text: stipendText.trim() || null,
      doors_open_text: doorsOpenText.trim() || null,
      provisions: Array.from(provisions),
      agenda_md: agendaMd.trim() || null,
      about_md: aboutMd.trim() || null,
      presenters_md: presentersMd.trim() || null,
      series_id: seriesId,
      sequence_number: sequenceNumber,
      instance_label: instanceLabel.trim() || null,
      prereq_event_ids: prereqEventIds,
      prereq_text: prereqText.trim() || null,
      slug: slug.trim() || null,
      days: days.length > 0 ? days : undefined,
      is_recurring_template: isRecurringTemplate,
      parent_template_id: parentTemplateId,
      is_draft: isDraft,
      instance_notes: instanceNotes.trim() || null,
      tag_ids: Array.from(selectedTags),
      // Task #15: online + hybrid fields. Always send so we can clear them
      // on edit (e.g., switching hybrid → in_person).
      format,
      online_provider: format === 'in_person' ? null : (onlineProvider || null),
      online_meeting_url: format === 'in_person' ? null : (onlineMeetingUrl.trim() || null),
      online_meeting_id: format === 'in_person' ? null : (onlineMeetingId.trim() || null),
      online_meeting_passcode: format === 'in_person' ? null : (onlineMeetingPasscode.trim() || null),
      online_join_instructions: format === 'in_person' ? null : (onlineJoinInstructions.trim() || null),
      online_url_visibility: onlineUrlVisibility,
      online_max_attendees: format === 'hybrid' ? (onlineMaxAttendees === '' ? null : Number(onlineMaxAttendees)) : null,
    };
    try {
      if (isEditMode && eventId) {
        // Edit: include resource_id so the backend can sync the linked
        // resource_booking. The backend archives the old booking(s) and
        // creates per-day bookings on the new resource. Setting null releases
        // the room.
        await api.updateEvent(token, eventId, {
          ...payload,
          resource_id: resourceId,
          // Task #3: always send the current set of staff so the backend
          // can sync per-(staff, day) bookings (added, removed, or replaced).
          staff_resource_ids: Array.from(staffResourceIds),
        });
        // Prefer the slug-based public URL since it's ID-system-agnostic.
        // Falls back to the admin events list if there's no slug.
        if (slug.trim()) {
          window.location.hash = `#e/${slug.trim()}`;
        } else {
          window.location.hash = `#admin/events`;
        }
      } else {
        await api.createEvent(token, {
          ...payload,
          resource_id: resourceId,
          staff_resource_ids: Array.from(staffResourceIds),
        });
        // Surface a success card with options instead of auto-navigating —
        // admins often want to create several events in a row.
        setLastCreated({ slug: slug.trim() || null, title, isDraft, isTemplate: isRecurringTemplate });
        setSubmitting(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    } catch (err: any) {
      setError(err?.message || (isEditMode ? 'Could not save changes.' : 'Could not create event.'));
      setSubmitting(false);
      // Scroll to top so the user sees the error banner (which renders at the
      // top of the form). On a long form like ours the user is almost always
      // scrolled past the banner when they hit Save.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const topicTags   = tags.filter((t) => t.tag_category === 'topic');
  const programTags = tags.filter((t) => t.tag_category === 'program');

  return (
    // Task #6: rendered inside the AdminLayout sidebar shell. No own
    // min-h-screen wrapper — let the layout own background + padding. Keep a
    // max-w-3xl container around the form contents for readable line length.
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit event' : 'Add an event'}
          </h1>
          <p className="text-sm text-gray-600 mt-0.5">
            {isEditMode ? 'Update event details.' : 'Create a new entry on the training calendar.'}
          </p>
        </div>
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>
      <div className="pb-20">{/* room for sticky save bar */}
        {loadingEvent && (
          <div className="bg-white border border-gray-200 rounded-md p-6 text-center text-gray-500">
            Loading event…
          </div>
        )}
        {/* Round 4: post-create success card with three choices */}
        {lastCreated && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-4 flex items-start gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-green-900">
                "{lastCreated.title}" was{' '}
                {lastCreated.isTemplate
                  ? 'saved as a recurring template'
                  : lastCreated.isDraft
                    ? 'saved as a draft'
                    : 'created and published'}
              </div>
              <p className="text-xs text-green-800 mt-0.5">
                {lastCreated.isTemplate
                  ? 'Templates are hidden from the public calendar and from the default Events list — find them under the Templates tab on the Events page. Use Series and templates → Materialize to generate real event instances from this template.'
                  : lastCreated.isDraft
                    ? 'Hidden from the public calendar until you Go Live.'
                    : 'Visible on the public calendar now.'}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {lastCreated.slug && !lastCreated.isTemplate && (
                  <a
                    href={`#e/${lastCreated.slug}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
                  >
                    <ArrowRight className="h-4 w-4" />
                    View this event
                  </a>
                )}
                {lastCreated.isTemplate && (
                  <a
                    href="#admin/series"
                    className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
                  >
                    <ArrowRight className="h-4 w-4" />
                    Go to Series and templates
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => {
                    // Force a clean remount of EventForm so all state resets.
                    window.location.hash = '#admin/events/new';
                    window.location.reload();
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-green-700 bg-white px-3 py-1.5 text-sm font-semibold text-green-800 hover:bg-green-100"
                >
                  <Plus className="h-4 w-4" />
                  Add another
                </button>
                <a
                  href={lastCreated.isTemplate ? '#admin/events?scope=templates' : '#admin/events'}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Back to events list
                </a>
              </div>
            </div>
          </div>
        )}

        {!loadingEvent && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
              {error}
            </div>
          )}
          {/* ----- BASICS ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Basics</legend>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Title</span>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={submitting}
                placeholder="e.g. Conservation Workshop"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Audience</span>
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value as Audience)}
                disabled={submitting}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              >
                {AUDIENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label} — {opt.description}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Description</span>
              <textarea
                rows={8}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={submitting}
                placeholder={
                  'Markdown supported. Examples:\n' +
                  '**bold text**   *italic text*\n' +
                  '- bullet item\n' +
                  '1. numbered item\n' +
                  '[link text](https://example.com)\n' +
                  '<center>centered line</center>'
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Markdown supported:&nbsp;
                <code className="bg-gray-100 px-1 rounded">**bold**</code>{' '}
                <code className="bg-gray-100 px-1 rounded">*italic*</code>{' '}
                <code className="bg-gray-100 px-1 rounded">- bullet</code>{' '}
                <code className="bg-gray-100 px-1 rounded">[link](url)</code>{' '}
                <code className="bg-gray-100 px-1 rounded">&lt;center&gt;…&lt;/center&gt;</code>{' '}
                — blank lines separate paragraphs.
              </p>
            </label>
          </fieldset>
          {/* ----- WHEN ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">When</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">Start</span>
                <input
                  type="datetime-local"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">End</span>
                <input
                  type="datetime-local"
                  required
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                />
              </label>
            </div>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Registration deadline (optional)</span>
              <input
                type="datetime-local"
                value={registrationDeadline}
                onChange={(e) => setRegistrationDeadline(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
          </fieldset>
          {/* ----- DAYS (Phase 028) ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Days</legend>
            <p className="text-xs text-gray-500">
              A single-day event uses just Day 1. Add more days for multi-day workshops.
              Each day inherits the event-level Start/End/doors-open by default; expand
              "Advanced" to override per day or add buffer time for setup/cleanup.
            </p>
            <DaysSection
              days={days.length > 0 ? days : [{
                day_index: 1,
                day_date: startDate ? startDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
              }]}
              onChange={setDays}
              disabled={submitting}
              fallbackStartTime={startDate.slice(11, 16) || '08:30'}
              fallbackEndTime={endDate.slice(11, 16) || '17:00'}
              fallbackDoorsOpen={doorsOpenText}
              fallbackProvisions={Array.from(provisions)}
            />
          </fieldset>
          {/* ----- SERIES (Phase 026) ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Series</legend>
            {token && (
              <SeriesPicker
                token={token}
                seriesId={seriesId}
                sequenceNumber={sequenceNumber}
                instanceLabel={instanceLabel}
                onChange={({ seriesId: s, sequenceNumber: sn, instanceLabel: il }) => {
                  setSeriesId(s); setSequenceNumber(sn); setInstanceLabel(il);
                }}
                disabled={submitting}
              />
            )}
          </fieldset>
          {/* ----- RECURRING TEMPLATE (Round 3) ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Recurring template</legend>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isRecurringTemplate}
                onChange={(e) => setIsRecurringTemplate(e.target.checked)}
                disabled={submitting || parentTemplateId !== null}
                className="h-4 w-4 accent-uif-green"
              />
              <span className="text-sm text-gray-700">
                This is a recurring template
                {parentTemplateId !== null && ' (locked — this event is itself an instance)'}
              </span>
            </label>
            <p className="text-xs text-gray-500">
              When checked, this event is hidden from the public calendar. Use the "Materialize instances" button on a
              recurring series to copy this template into N child events with their own dates.
            </p>
            {parentTemplateId !== null && (
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">Instance notes</span>
                <textarea
                  rows={3}
                  value={instanceNotes}
                  onChange={(e) => setInstanceNotes(e.target.value)}
                  disabled={submitting}
                  placeholder="e.g. This week's focus: postharvest cooling demo"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Shown only on this instance. Editing the parent template does not propagate to existing instances.
                </p>
              </label>
            )}
          </fieldset>
          {/* ----- WHERE ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Where</legend>
            {token && (
              <LocationLibrary
                token={token}
                currentAddress={location}
                onPick={(l) => {
                  if (l.address) setLocation(l.address);
                  if (l.venue_details) setVenueDetails(l.venue_details);
                }}
                disabled={submitting}
              />
            )}
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Location (address)</span>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Venue details (optional)</span>
              <input
                type="text"
                value={venueDetails}
                onChange={(e) => setVenueDetails(e.target.value)}
                disabled={submitting}
                placeholder="e.g. Coffee at 8am, lunch provided"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Reserve a room (optional)</span>
              <select
                value={resourceId ?? ''}
                onChange={(e) => setResourceId(e.target.value ? Number(e.target.value) : null)}
                disabled={submitting || resources.length === 0}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              >
                <option value="">No room reservation</option>
                {resources
                  .filter((r) => r.resource_type !== 'staff')
                  .map((r) => (
                    <option key={r.resource_id} value={r.resource_id}>
                      {r.resource_name}{r.capacity ? ` (capacity ${r.capacity})` : ''}
                    </option>
                  ))}
              </select>
              {resourceId && (
                <p className="text-xs text-gray-500 mt-1">
                  A confirmed booking will be auto-created for this room covering the event's date range.
                </p>
              )}
            </label>

            {/* Task #3: Assigned staff multi-select. Same conflict detection
                as rooms — assigning someone already booked elsewhere returns
                409 'resource_conflict'. Shows only staff resources; manage
                the roster from Admin → Resources. */}
            {resources.some((r) => r.resource_type === 'staff') && (
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned staff (optional)
                </span>
                <div className="flex flex-wrap gap-2">
                  {resources
                    .filter((r) => r.resource_type === 'staff')
                    .map((r) => {
                      const selected = staffResourceIds.has(r.resource_id);
                      return (
                        <button
                          type="button"
                          key={r.resource_id}
                          disabled={submitting}
                          onClick={() => {
                            const next = new Set(staffResourceIds);
                            if (next.has(r.resource_id)) next.delete(r.resource_id);
                            else next.add(r.resource_id);
                            setStaffResourceIds(next);
                          }}
                          className={
                            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ' +
                            (selected
                              ? 'bg-uif-green text-white border-uif-green'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-uif-green hover:text-uif-green-dark')
                          }
                          style={
                            selected && r.color_hex
                              ? { backgroundColor: r.color_hex, borderColor: r.color_hex }
                              : undefined
                          }
                        >
                          {selected && <span aria-hidden>✓</span>}
                          {r.resource_name}
                        </button>
                      );
                    })}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Each selected staff member gets a confirmed booking for this event. Assigning someone already booked elsewhere in this window will be blocked.
                </p>
              </div>
            )}
          </fieldset>

          {/* ----- FORMAT & DELIVERY (Task #15) ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Format &amp; delivery</legend>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Format</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'in_person' as const, label: 'In person', desc: 'Physical attendance only' },
                  { value: 'online'    as const, label: 'Online',    desc: 'Online only — virtual meeting' },
                  { value: 'hybrid'    as const, label: 'Hybrid',    desc: 'Both in-person and online' },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => setFormat(opt.value)}
                    disabled={submitting}
                    className={
                      'inline-flex flex-col items-start gap-0.5 rounded-lg border px-3 py-2 text-left transition ' +
                      (format === opt.value
                        ? 'bg-uif-green text-white border-uif-green'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-uif-green hover:text-uif-green-dark')
                    }
                  >
                    <span className="text-sm font-semibold">{opt.label}</span>
                    <span className={'text-[11px] ' + (format === opt.value ? 'text-white/80' : 'text-gray-500')}>{opt.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {(format === 'online' || format === 'hybrid') && (
              <div className="space-y-3 border-t border-gray-200 pt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Meeting provider</span>
                    <select
                      value={onlineProvider}
                      onChange={(e) => setOnlineProvider(e.target.value as any)}
                      disabled={submitting}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                    >
                      <option value="">— pick one —</option>
                      <option value="zoom">Zoom</option>
                      <option value="teams">Microsoft Teams</option>
                      <option value="meet">Google Meet</option>
                      <option value="webex">Webex</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Meeting URL</span>
                    <input
                      type="url"
                      value={onlineMeetingUrl}
                      onChange={(e) => setOnlineMeetingUrl(e.target.value)}
                      disabled={submitting}
                      placeholder="https://zoom.us/j/…"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Meeting ID (optional)</span>
                    <input
                      type="text"
                      value={onlineMeetingId}
                      onChange={(e) => setOnlineMeetingId(e.target.value)}
                      disabled={submitting}
                      placeholder="123 456 7890"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Passcode (optional)</span>
                    <input
                      type="text"
                      value={onlineMeetingPasscode}
                      onChange={(e) => setOnlineMeetingPasscode(e.target.value)}
                      disabled={submitting}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">Join instructions (optional)</span>
                  <textarea
                    value={onlineJoinInstructions}
                    onChange={(e) => setOnlineJoinInstructions(e.target.value)}
                    disabled={submitting}
                    rows={2}
                    placeholder="e.g. Please join 10 minutes early to test audio. Dial-in: +1-555-555-1234."
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Appears in the confirmation email below the Join button.</p>
                </label>

                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">Meeting URL visibility</span>
                  <div className="space-y-2">
                    {[
                      { value: 'registered_only' as const,  label: 'Registered only', desc: 'Sent in confirmation email. Public detail page shows "Register to receive link". Recommended.' },
                      { value: 'farmer_signed_in' as const, label: 'Producer account required', desc: 'Only farmers signed into events.upinfarms.com can see the link or join.' },
                      { value: 'public' as const,           label: 'Public', desc: 'URL shown on the public event page to anyone. Use for open community events.' },
                    ].map((opt) => (
                      <label key={opt.value} className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="online_url_visibility"
                          value={opt.value}
                          checked={onlineUrlVisibility === opt.value}
                          onChange={() => setOnlineUrlVisibility(opt.value)}
                          disabled={submitting}
                          className="mt-1"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-800">{opt.label}</div>
                          <div className="text-xs text-gray-500">{opt.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {format === 'hybrid' && (
                  <label className="block">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Online seats cap (optional)</span>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      value={onlineMaxAttendees}
                      onChange={(e) => setOnlineMaxAttendees(e.target.value === '' ? '' : Number(e.target.value))}
                      disabled={submitting}
                      placeholder="Leave blank for no separate cap"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-uif-green focus:ring-1 focus:outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Caps how many people can register online. Overall <em>Max attendees</em> still applies to the combined in-person + online count.
                    </p>
                  </label>
                )}
              </div>
            )}
          </fieldset>

          {/* ----- COST + STIPEND + DOORS OPEN ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Cost &amp; arrival</legend>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Cost</span>
              <input
                type="text"
                value={costText}
                onChange={(e) => setCostText(e.target.value)}
                disabled={submitting}
                placeholder="Free (default if left blank)"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Examples: <em>Free</em> · <em>$50 sliding scale</em> · <em>Free for AMP producers, $25 others</em>
              </p>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Stipend / honorarium (optional)</span>
              <input
                type="text"
                value={stipendText}
                onChange={(e) => setStipendText(e.target.value)}
                disabled={submitting}
                placeholder="e.g. $100 total for both days · $50 + 1-year Grow-It license"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                What the attendee receives. Leave blank if there's no stipend.
              </p>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Doors open / arrival info (optional)</span>
              <input
                type="text"
                value={doorsOpenText}
                onChange={(e) => setDoorsOpenText(e.target.value)}
                disabled={submitting}
                placeholder="e.g. 8:00am — coffee &amp; registration  ·  8am day 1, 9am day 2"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Defaults to "8:00am — coffee &amp; registration" if blank. Use prose for per-day arrival times.
              </p>
            </label>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">Provisions (optional)</span>
              <div className="flex flex-wrap gap-2">
                {PROVISION_OPTIONS.map((opt) => {
                  const selected = provisions.has(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleProvision(opt.value)}
                      disabled={submitting}
                      className={
                        'rounded-full px-3 py-1 text-xs border transition-colors ' +
                        (selected
                          ? 'bg-uif-green text-white border-uif-green-dark'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                      }
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Tap any provision included with the event. Selected items appear as badges on the event page.
                When a meal is included, registration will collect dietary restrictions.
              </p>
            </div>
          </fieldset>
          {/* ----- CAPACITY + EXTRAS ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Capacity + details</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">Max attendees (optional)</span>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={maxAttendees}
                  onChange={(e) => setMaxAttendees(e.target.value === '' ? '' : Number(e.target.value))}
                  disabled={submitting}
                  placeholder="16"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">Event type</span>
                <input
                  type="text"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value)}
                  disabled={submitting}
                  placeholder="Workshop"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                />
              </label>
            </div>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">What to bring (optional)</span>
              <input
                type="text"
                value={materials}
                onChange={(e) => setMaterials(e.target.value)}
                disabled={submitting}
                placeholder="Notebook, current recordkeeping samples"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">URL slug</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 whitespace-nowrap">events.upinfarms.com/#e/</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }}
                  disabled={submitting}
                  placeholder="auto-suggested from title + date"
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Persistent public URL. Lowercase letters / digits / dashes only. Default format is&nbsp;
                <code className="bg-gray-100 px-1 rounded">{`<title>-YYYYMMDD`}</code>;
                shorten it (e.g. <code className="bg-gray-100 px-1 rounded">food-safety-jun-2026</code>) if you want a cleaner URL for ads.
              </p>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Source reference key</span>
              <input
                type="text"
                value={sourceKey}
                onChange={(e) => { setSourceKey(e.target.value); setSourceKeyManual(true); }}
                disabled={submitting}
                placeholder="auto-suggested from title + date"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Auto-suggested from the title + start month. Internal-only lookup key (used by SQL queries). Edit only if you have a specific reason to override it.
              </p>
            </label>
          </fieldset>
          {/* ----- PREREQUISITES (Phase 026) ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Prerequisites</legend>
            {token && (
              <PrerequisitePicker
                token={token}
                currentEventId={eventId}
                selectedIds={prereqEventIds}
                prereqText={prereqText}
                onChange={({ selectedIds, prereqText: t }) => {
                  setPrereqEventIds(selectedIds);
                  setPrereqText(t);
                }}
                disabled={submitting}
              />
            )}
          </fieldset>
          {/* ----- TAGS ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Tags</legend>
            <p className="text-xs text-gray-500">Click pills to tag. Multiple tags allowed. Producers can filter the calendar by tag.</p>
            {topicTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {topicTags.map((t) => (
                  <button
                    key={t.tag_id}
                    type="button"
                    onClick={() => toggleTag(t.tag_id)}
                    disabled={submitting}
                    className={
                      'rounded-full px-3 py-1 text-xs border transition-colors ' +
                      (selectedTags.has(t.tag_id)
                        ? 'bg-uif-green text-white border-uif-green-dark'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                    }
                  >
                    {t.tag_name}
                  </button>
                ))}
              </div>
            )}
            {programTags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {programTags.map((t) => (
                  <button
                    key={t.tag_id}
                    type="button"
                    onClick={() => toggleTag(t.tag_id)}
                    disabled={submitting}
                    className={
                      'rounded-full px-3 py-1 text-xs border transition-colors font-medium ' +
                      (selectedTags.has(t.tag_id)
                        ? 'bg-uif-brown text-white border-uif-brown'
                        : 'bg-white text-uif-brown-dark border-uif-brown/40 hover:bg-uif-brown/5')
                    }
                  >
                    {t.tag_name}
                  </button>
                ))}
              </div>
            )}
          </fieldset>
          {/* ----- MODAL CONTENT ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Agenda / About / Presenters (optional)</legend>
            <p className="text-xs text-gray-500">
              Each field below populates one of the three pop-up modals on the event detail page.
              All three are markdown — same syntax as the Description field above. Leave a field
              empty to hide that button entirely.
            </p>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Agenda</span>
              <textarea
                rows={10}
                value={agendaMd}
                onChange={(e) => setAgendaMd(e.target.value)}
                disabled={submitting}
                placeholder={
                  'Markdown supported. Tables work well for a time-grid:\n\n' +
                  '## Day 1\n' +
                  '| Time | Session | Presenter |\n' +
                  '|------|---------|-----------|\n' +
                  '| 8:30 AM | Welcome | David |\n'
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use markdown tables for the time-grid look. Leave blank to hide the "View agenda" button.
              </p>
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">About the event</span>
              <textarea
                rows={10}
                value={aboutMd}
                onChange={(e) => setAboutMd(e.target.value)}
                disabled={submitting}
                placeholder={
                  'Long-form description shown in the "About" modal.\n\n' +
                  '## Headline\n\n' +
                  '*Subhead in italic*\n\n' +
                  '> Pull-quote in blockquote\n\n' +
                  'Body paragraph...\n'
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                The deep-dive content — what's covered, what makes it different, what attendees walk away with.
                Leave blank to hide the "About the workshop" button.
              </p>
            </label>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Presenters</span>
              {token && (
                <PresenterLibrary
                  token={token}
                  value={presentersMd}
                  onChange={setPresentersMd}
                  disabled={submitting}
                />
              )}
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">Markdown source</span>
              <textarea
                rows={10}
                value={presentersMd}
                onChange={(e) => setPresentersMd(e.target.value)}
                disabled={submitting}
                placeholder={
                  'One section per presenter, separated by --- horizontal rules:\n\n' +
                  '## Kayla McMinn\n' +
                  '*Food Safety Specialist · Day 1 lead*\n\n' +
                  '![Kayla](/presenters/kayla.jpg)\n\n' +
                  "Kayla's bio...\n\n" +
                  '---\n\n' +
                  '## Bridget Y. Townsend\n' +
                  '...'
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use <code className="bg-gray-100 px-1 rounded">![alt](/path/to/photo.jpg)</code> for headshots.
                Separate presenters with <code className="bg-gray-100 px-1 rounded">---</code>.
                Leave blank to hide the "Meet the presenters" button.
              </p>
            </label>
          </fieldset>
          {/* ----- MEDIA ----- */}
          <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <legend className="px-2 text-sm font-semibold text-uif-green-dark">Media (optional)</legend>
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">Hero photo</span>
              {token && (
                <UploadPhoto token={token} value={heroPhotoUrl} onChange={setHeroPhotoUrl} destination="events" shape="banner" disabled={submitting} />
              )}
              <label className="block mt-2">
                <span className="block text-xs font-medium text-gray-500 mb-1">…or paste a URL manually</span>
                <input type="url" value={heroPhotoUrl} onChange={(e) => setHeroPhotoUrl(e.target.value)} disabled={submitting}
                  placeholder="https://events.upinfarms.com/uploads/events/..."
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none text-sm" />
              </label>
              <p className="text-xs text-gray-500 mt-1">Renders as a 21:9 banner at the top of the event detail page.</p>
            </div>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">YouTube embed URL</span>
              <input type="url" value={videoEmbedUrl} onChange={(e) => setVideoEmbedUrl(e.target.value)} disabled={submitting}
                placeholder="https://www.youtube.com/embed/dQw4w9WgXcQ"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
              <p className="text-xs text-gray-500 mt-1">Use the /embed/ URL form.</p>
            </label>
          </fieldset>

          {/* Task #49: Evidence attachments (paper sign-in sheets, room
              photos, signed forms). Only shown when editing an existing
              event — we need a calendar_entry_id to attach against. */}
          {isEditMode && eventId && token && (
            <fieldset className="bg-white border border-gray-200 rounded-lg p-5 space-y-3">
              <legend className="px-2 text-sm font-semibold text-uif-green-dark">Evidence &amp; attachments</legend>
              <p className="text-xs text-gray-500">
                Snap photos of paper sign-in sheets, signed forms, or room shots. On a phone, the
                camera opens directly. Files are stored privately to UIF and tagged to a day if the
                event spans multiple days.
              </p>
              <EvidenceUploadInline
                calendarEntryId={eventId}
                token={token}
                dayCount={Math.max(1, days.length || 1)}
              />
            </fieldset>
          )}
          {/* Round 4: Draft toggle. Defaults to draft on create so accidental
              publishing is impossible. Edit mode preserves the loaded value. */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <input
              type="checkbox"
              id="event-form-is-draft"
              checked={isDraft}
              onChange={(e) => setIsDraft(e.target.checked)}
              disabled={submitting}
              className="mt-1 h-4 w-4 accent-amber-600"
            />
            <label htmlFor="event-form-is-draft" className="text-sm flex-1 cursor-pointer">
              <span className="font-semibold text-amber-900">Save as draft (preview mode)</span>
              <span className="block text-xs text-amber-800 mt-0.5">
                {isDraft
                  ? 'Hidden from the public calendar until you Go Live. Visible to UIF admins only.'
                  : 'This event will be LIVE on the public calendar immediately after save.'}
              </span>
            </label>
          </div>
          {/* Task #6: sticky save bar pinned to the bottom of the viewport so
              the primary action stays reachable on this long form. */}
          <div className="sticky bottom-0 -mx-4 sm:mx-0 sm:rounded-lg bg-white border-t sm:border border-gray-200 px-4 py-3 mt-4 flex flex-wrap justify-end gap-2 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]">
            <button type="button" onClick={handleBack} disabled={submitting}
              className="rounded-md border border-gray-300 px-5 py-2.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={submitting || !title}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-6 py-2.5 hover:bg-uif-green-dark disabled:bg-gray-300">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEditMode ? 'Save changes' : isDraft ? 'Create draft' : 'Create and publish'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
